import React, { useState, useEffect, useCallback } from 'react'

import { useNavigate, useSearchParams } from 'react-router-dom'

import { PeriodCycle } from '@/core/api/dtos/request/account/updateSubscription'
import { Subscription } from '@/core/api/entities/account/subscription'
import { Zone } from '@/core/api/entities/zone/zone'
import { useRefetchAccountData } from '@/core/hooks/account/useRefetchAccountData'
import {
	useUpdateSubscription,
	useGetSubscriptionPlans,
} from '@/core/hooks/account/useSubscription'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Badge } from '@/shared/components/atoms/Badge/Badge'
import { Button } from '@/shared/components/atoms/Button'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	Card,
	CardContent,
	CardTitle,
} from '@/shared/components/molecules/Card/Card'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'
import { extractErrorMessage } from '@/shared/utils/helpers'

import { ManageZonesModal } from './ManageZonesModal'
import { UpgradePlanModal } from './UpgradePlanModal'

export interface SubscriptionCardProps {
	planName?: string
	activeZonesCount?: number // The number of zones currently active
	totalPrice?: number
	pricePerZone?: number | null
	currency?: string
	periodCycle?: string
	nextBillingAt?: string | null
	trialEndAt?: string | null
	status?: string
	isInTrial?: boolean
	/** List of zones for the remove zones modal */
	zones?: Zone[]
	/** Whether zones are loading */
	zonesLoading?: boolean
	/** Callback when zones count is updated */
	onUpdateZones?: (
		newZonesCount: number,
		zonesToRemove?: number[]
	) => Promise<void>
	subscription?: Subscription
}

export const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
	planName,
	activeZonesCount = 0,
	totalPrice = 0,
	pricePerZone,
	currency = 'USD',
	periodCycle = 'monthly',
	nextBillingAt,
	trialEndAt,
	isInTrial,
	zones = [],
	zonesLoading = false,
	onUpdateZones,
	subscription,
}) => {
	const { t, currentLanguage } = useTranslation('common')
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()
	const { showNotification } = useNotificationContext()
	const refetchAccountData = useRefetchAccountData()
	const account = useCurrentAccount()
	const [showUpgradeModal, setShowUpgradeModal] = useState(false)
	const [showManageZonesModal, setShowManageZonesModal] = useState(false)

	// Fetch subscription plans
	const { data: subscriptionPlans = [], isLoading: isLoadingPlans } =
		useGetSubscriptionPlans({
			config: useAccountConfig({
				params: {
					include: 'prices',
				},
			}),
		})

	// Hook for updating subscription
	const { mutateAsync: updateSubscription } = useUpdateSubscription({
		subscriptionId: subscription?.id || subscription?.id || 0,
		options: {
			onSuccess: () => {
				refetchAccountData()
			},
			onError: (error: any) => {
				console.error('❌ Subscription update failed:', error)
				showNotification({
					type: 'error',
					message: t('account.upgrade_modal.error', {
						defaultValue: 'Failed to update subscription',
					}),
					description: extractErrorMessage(
						error,
						t('common.try_again_later', { defaultValue: 'Please try again' })
					),
				})
			},
		},
	})

	const formatDate = (dateString: string | null | undefined) => {
		if (!dateString) return ''
		return new Date(dateString).toLocaleDateString(currentLanguage, {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		})
	}

	const renderNextInvoiceDate = () => {
		if (nextBillingAt) {
			return (
				<>
					{t('account.subscription.next_invoice', {
						date: formatDate(nextBillingAt),
					})}
				</>
			)
		}

		if (isInTrial && trialEndAt) {
			return (
				<>
					{t('account.subscription.trial_ends_on', {
						date: formatDate(trialEndAt),
					})}
				</>
			)
		}

		return null
	}

	// ✅ Validation: Check requirements to modify subscription
	const canModifySubscription = useCallback(() => {
		if (!subscription) {
			showNotification({
				type: 'error',
				message: t('account.upgrade_modal.no_subscription', {
					defaultValue: 'No active subscription found',
				}),
				description: t('account.upgrade_modal.contact_support', {
					defaultValue: 'Please contact support.',
				}),
			})
			return false
		}

		// Check if billing data exists
		const hasBillingData = account?.billingInfo?.hasBillingData()
		if (!hasBillingData) {
			showNotification({
				type: 'error',
				message: t('account.upgrade_modal.error_title', {
					defaultValue: 'Cannot modify subscription',
				}),
				description: t('account.upgrade_modal.no_billing_data', {
					defaultValue:
						'You must complete your billing information before modifying your subscription. Please add your billing details first.',
				}),
			})
			return false
		}

		// Check if subscription is promotional/free
		const isBonificada = subscription.total === 0
		if (isBonificada) {
			showNotification({
				type: 'error',
				message: t('account.upgrade_modal.error_title', {
					defaultValue: 'Cannot modify subscription',
				}),
				description: t('account.upgrade_modal.bonificada_subscription', {
					defaultValue:
						'Your account has a promotional subscription that cannot be modified through self-service. Please contact support for assistance.',
				}),
			})
			return false
		}

		return true
	}, [subscription, account, t, showNotification])

	// Check for auto-open upgrade modal from URL
	useEffect(() => {
		const action = searchParams.get('action')
		if (action === 'upgrade' && subscription && !isLoadingPlans) {
			if (canModifySubscription()) {
				setShowUpgradeModal(true)
			}
			// Always remove the param to prevent re-triggering
			setSearchParams(
				(prev) => {
					const newParams = new URLSearchParams(prev)
					newParams.delete('action')
					return newParams
				},
				{ replace: true }
			)
		}
	}, [
		searchParams,
		subscription,
		isLoadingPlans,
		canModifySubscription,
		setSearchParams,
	])

	// Handler to open upgrade modal (with validations)
	const handleOpenUpgradeModal = () => {
		if (canModifySubscription()) {
			setShowUpgradeModal(true)
		}
	}

	// Handler for upgrading plan
	const handleUpgradePlan = async (planId: string) => {
		if (!subscription) {
			showNotification({
				type: 'error',
				message: t('account.upgrade_modal.no_subscription', {
					defaultValue: 'No active subscription found',
				}),
				description: t('account.upgrade_modal.contact_support', {
					defaultValue: 'Please contact support.',
				}),
			})
			return
		}

		// ✅ Validation 1: Check if billing data exists
		const hasBillingData = account?.billingInfo?.hasBillingData()
		if (!hasBillingData) {
			showNotification({
				type: 'error',
				message: t('account.upgrade_modal.error_title', {
					defaultValue: 'Cannot modify subscription',
				}),
				description: t('account.upgrade_modal.no_billing_data', {
					defaultValue:
						'You must complete your billing information before modifying your subscription. Please add your billing details first.',
				}),
			})
			return
		}

		// ✅ Validation 2: Check if subscription is promotional/free
		const isBonificada = subscription.total === 0
		if (isBonificada) {
			showNotification({
				type: 'error',
				message: t('account.upgrade_modal.error_title', {
					defaultValue: 'Cannot modify subscription',
				}),
				description: t('account.upgrade_modal.bonificada_subscription', {
					defaultValue:
						'Your account has a promotional subscription that cannot be modified through self-service. Please contact support for assistance.',
				}),
			})
			return
		}

		// Check if plans are loaded
		if (isLoadingPlans) {
			showNotification({
				type: 'info',
				message: t('account.upgrade_modal.loading_plans', {
					defaultValue: 'Loading plans...',
				}),
			})
			return
		}

		// Get backend plan ID from API data by matching plan id
		const backendPlan = subscriptionPlans.find(
			(p) => p.name.toLowerCase() === planId.toLowerCase()
		)

		if (!backendPlan) {
			showNotification({
				type: 'error',
				message: t('account.upgrade_modal.plan_not_found', {
					defaultValue: 'Plan not found',
				}),
				description: t('account.upgrade_modal.plan_not_found_description', {
					plan: planId,
					defaultValue: `Could not find plan "${planId}" in available plans.`,
				}),
			})
			return
		}

		const playerItems = subscription.getPlayerItems()
		if (playerItems.length === 0) {
			showNotification({
				type: 'error',
				message: t('account.upgrade_modal.no_items', {
					defaultValue: 'No subscription items found',
				}),
			})
			return
		}

		// Determine period cycle from current subscription
		const periodCycle =
			subscription.periodCycle === 'yearly'
				? PeriodCycle.YEARLY
				: PeriodCycle.MONTHLY

		// Get current quantity
		const currentQuantity = subscription.getPlayerQuantity() ?? activeZonesCount

		await updateSubscription({
			plan_id: backendPlan.id,
			period_cycle: periodCycle,
			items: playerItems.map((item) => ({
				id: item.id,
				quantity: currentQuantity,
			})),
		})
	}

	return (
		<Card className="bg-background">
			<CardContent className="p-4">
				<div className="flex justify-between items-start">
					<CardTitle className="text-medium mb-[19px] flex items-center gap-2">
						{planName || t('account.billing.enterprise_plan')}
						{isInTrial && (
							<Badge
								variant="secondary"
								className="text-sm border-main-accent text-main-accent rounded-sm"
							>
								{t('account.billing.trial')}
							</Badge>
						)}
					</CardTitle>
					<Badge
						variant="secondary"
						className="text-sm bg-background text-content-secondary rounded-full"
					>
						{activeZonesCount} {t('account.billing.zones')}
					</Badge>
				</div>

				{/* Financial Details Section */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
					{/* Price per zone */}
					<div>
						<p className="text-sm text-content-tertiary mb-1">
							{t('account.subscription.price_per_zone')}
						</p>
						<div className="text-3xl font-bold">
							<span className="text-4xl">
								{pricePerZone !== null && pricePerZone !== undefined
									? pricePerZone.toFixed(2)
									: '-'}
							</span>
							<span className="text-lg font-normal"> {currency}</span>
						</div>
					</div>

					{/* Total price */}
					<div>
						<p className="text-sm text-content-tertiary mb-1">
							{t('account.billing.total_price')}
						</p>
						<div className="text-3xl font-bold">
							<span className="text-4xl">{totalPrice}</span>
							<span className="text-lg font-normal">
								.00 {currency} {t('account.billing.billed')}{' '}
								{t(`entities.billing.cycles.word.${periodCycle}`).toLowerCase()}
							</span>
						</div>
					</div>
				</div>

				{/* Separator */}
				<Separator className="my-6" />

				{/* Action Buttons and Next Invoice */}
				<div className="flex flex-col items-center lg:items-start md:flex-row justify-between md:items-center gap-4">
					<div className="w-full flex flex-col sm:flex-row gap-3">
						{isInTrial ? (
							<Button
								className="rounded-full cursor-pointer"
								onClick={() => navigate('/subscribe')}
							>
								{t('trial.subscribe', { defaultValue: 'Subscribe' })}
							</Button>
						) : (
							<div className="w-full flex flex-col sm:flex-row gap-3">
								<Button
									className="w-full sm:w-auto rounded-full cursor-pointer"
									onClick={handleOpenUpgradeModal}
								>
									{t('account.upgrade_plan')}
								</Button>
								<Button
									variant="outline"
									className="rounded-full cursor-pointer"
									onClick={() => {
										if (canModifySubscription()) {
											setShowManageZonesModal(true)
										}
									}}
								>
									{t('account.manage_zones')}
								</Button>
							</div>
						)}
					</div>
					<div className="text-sm text-content-tertiary">
						{renderNextInvoiceDate()}
					</div>
				</div>
			</CardContent>

			{!isInTrial && (
				<>
					{/* Upgrade Plan Modal */}
					<UpgradePlanModal
						visible={showUpgradeModal}
						onClose={() => setShowUpgradeModal(false)}
						onUpgrade={handleUpgradePlan}
						currentPlanId={subscription?.plan?.name?.toLowerCase()}
						currentPlanName={planName || 'Brandtrack Essential'}
						subscription={subscription}
						zonesCount={activeZonesCount}
						billingCycle={t(
							`entities.billing.cycles.word.${periodCycle}`
						).toLowerCase()}
						subscriptionPlans={subscriptionPlans}
					/>

					{/* Manage Zones Modal */}
					<ManageZonesModal
						visible={showManageZonesModal}
						onClose={() => setShowManageZonesModal(false)}
						onConfirm={async (newZonesCount, zonesToRemove) => {
							if (onUpdateZones) {
								await onUpdateZones(newZonesCount, zonesToRemove)
							} else {
								console.log(
									'Update zones count to:',
									newZonesCount,
									'Remove zones:',
									zonesToRemove
								)
							}
						}}
						planName={planName || 'Essential'}
						currentZonesCount={activeZonesCount}
						activeZonesCount={activeZonesCount}
						pricePerZone={pricePerZone || 0}
						currency={currency}
						billingCycle={t(
							`entities.billing.cycles.word.${periodCycle}`
						).toLowerCase()}
						zones={zones}
						zonesLoading={zonesLoading}
						isInTrial={isInTrial}
						onUpgrade={() => {
							setShowManageZonesModal(false)
							if (canModifySubscription()) {
								setShowUpgradeModal(true)
							}
						}}
					/>
				</>
			)}
		</Card>
	)
}

export default SubscriptionCard
